import { Button, Image, InlineStack, Text } from '@customerio/pluma-components/react';
import type { SsoConfiguration } from '@/services/oauth';
import { GoogleAuthButton, SSO_PROVIDER_ICON, SSO_PROVIDER_LABEL } from '@/components/sso-sign-in-button';

type SsoSignInButtonProps = {
  /** The discovered SSO config, or null for a Google one-click user with no required-SSO config. */
  ssoConfiguration: SsoConfiguration | null;
  /** Render the Google-branded button (Google IdP or one-click). */
  isGoogle: boolean;
  isLoading: boolean;
  onClick: () => void;
};

/**
 * The identity-provider sign-in button for the re-auth modal — a Google-branded
 * button for Google (IdP or one-click) and a labelled, logo-marked button for
 * Okta / Azure / OpenID / SAML.
 */
export function SsoSignInButton({ ssoConfiguration, isGoogle, isLoading, onClick }: SsoSignInButtonProps) {
  if (isGoogle) {
    // The re-auth modal uses Google's dark button (Ember's `@theme='dark'`).
    return <GoogleAuthButton theme="dark" isLoading={isLoading} onClick={onClick} data-testid="reauth-sso-submit" />;
  }

  // A required-SSO user whose config isn't loaded yet (discovery blip): a plain
  // "Log in" that re-fetches the config on submit.
  if (ssoConfiguration == null) {
    return (
      <Button type="button" variant="primary" isLoading={isLoading} onClick={onClick} data-testid="reauth-sso-submit">
        Log in
      </Button>
    );
  }

  const provider = ssoConfiguration.identity_provider;
  const icon = SSO_PROVIDER_ICON[provider];
  return (
    <Button type="button" variant="primary" isLoading={isLoading} onClick={onClick} data-testid="reauth-sso-submit">
      <InlineStack alignBlock="center" gap="100">
        {icon != null ? <Image src={icon} alt="" size="icon-v2-md" /> : null}
        <Text>{SSO_PROVIDER_LABEL[provider] ?? 'Sign in'}</Text>
      </InlineStack>
    </Button>
  );
}
